import { EventObject } from '@dustin-web/microsite-components/src/types'
import { mapCartProductData, mapProductData, mapProductDataLegacy } from './digital-data-mappers'
import {
  GA4AddingOrRemovingElement,
  GA4AddToCartEvent,
  GA4RemoveFromCartEvent,
  GA4AddToWishlistEvent,
  Props,
  GA4ViewItemListEvent,
  GA4SelectItemEvent,
  GA4ViewPromotionEvent,
  GA4SelectPromotionEvent,
  GA4ViewItemEvent,
  GA4ViewCartEvent,
  GA4Promotion,
  DigitalDataOrigin,
  BeginCheckoutType,
} from './digital-data-types'
import { isNotNullOrUndefined } from '@dustin-web/microsite-data/src/utils'
import { ProductWithCategoryType } from './digital-data-types'
import { getProductLabels } from '../cards/utils'
import { CartBaseProduct } from '../hooks/cart/cart-base-product-types'
import { CartItemFragment } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DigitalData: any
  }
}
export type EventListType = {
  quantity: number
  productListIndex?: number
} & CartBaseProduct
export const ProductDigitalData = {
  _init: ({ navigation }: Props) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    window.DigitalData.push({
      PageInfo: {
        BreadCrumbs: navigation.path.map(node => ({ Name: node.category?.name })),
      },
    })
  },

  //legacy event
  pushEvent: (
    eventCategory: string,
    eventAction: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eventLabel: any = undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eventValue: any = undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eventSource: any = undefined
  ) => {
    window.DigitalData = window.DigitalData || []

    window.DigitalData.push({
      event: eventAction,
      eventCategory,
      eventLabel,
      eventSource,
      eventValue,
    })
  },

  //legacy event
  _pushCartEvent: (
    event: 'addToCart' | 'removeFromCart',
    products: ProductWithCategoryType[],
    quantityDiff: number,
    translate: (key: string) => string,
    origin?: DigitalDataOrigin
  ) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const productsData = products.map(product => {
      const categoryPaths = product.categoryPathEng?.split('/')?.filter(Boolean)
      const category =
        categoryPaths && categoryPaths.length > 0
          ? categoryPaths[categoryPaths.length - 1]
          : undefined
      const productData = mapProductDataLegacy(product, category, translate)
      return { ...productData, Quantity: productData.Quantity || quantityDiff }
    })
    const originUrl =
      typeof window !== 'undefined' && !window.location.pathname.includes('/product/')
        ? window.location.pathname
        : null

    const originField = origin || originUrl
    const actionField = { list: originField }

    const addObject = originField
      ? { add: { actionField, products: productsData } }
      : { add: { products: productsData } }

    const removeObject = originField
      ? { remove: { actionField, products: productsData } }
      : { remove: { products: productsData } }

    const addOrRemoveObj = event === 'addToCart' ? addObject : removeObject

    const result = {
      event,
      ecommerce: {
        ...addOrRemoveObj,
      },
    }

    window.DigitalData.push(result)
  },

  pushAddToCart: (
    products: ProductWithCategoryType[],
    quantityDiff: number,
    translate: (key: string) => string,
    origin: DigitalDataOrigin | undefined,
    addingElementOrigin: GA4AddingOrRemovingElement,
    digitalDataCartIndex?: number
  ) => {
    // Legacy event
    ProductDigitalData._pushCartEvent('addToCart', products, quantityDiff, translate, origin)

    // GA4
    const eventName = 'add_to_cart'
    const items = products.map(p => {
      const updateObjWithQuantity = { ...p, quantity: quantityDiff, digitalDataCartIndex }
      return mapProductData(updateObjWithQuantity, origin, eventName, {
        add_to_cart_order: digitalDataCartIndex,
        unique_quantity: 1,
      })
    })

    const event: GA4AddToCartEvent = {
      event: eventName,
      ecommerce: { items },
      add_element: addingElementOrigin,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },
  pushAddMultipleProductsCartEvent: (
    products: EventListType[],
    origin: DigitalDataOrigin | undefined,
    addingElementOrigin: GA4AddingOrRemovingElement,
    digitalDataCartIndex?: number
  ) => {
    const eventName = 'add_to_cart'
    const items = products.map(item => ({
      ...mapProductData(item, origin, eventName, {
        add_to_cart_order: digitalDataCartIndex,
        unique_quantity: 1,
      }),
    }))
    const event: GA4AddToCartEvent = {
      event: eventName,
      ecommerce: { items },
      add_element: addingElementOrigin,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },
  pushRemoveFromCart: (
    products: ProductWithCategoryType[],
    quantityDiff: number,
    translate: (key: string) => string,
    currency: string,
    value: number,
    origin?: DigitalDataOrigin,
    digitalDataCartIndex?: number
  ) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    // Legacy event
    ProductDigitalData._pushCartEvent('removeFromCart', products, quantityDiff, translate, origin)

    //GA4
    const eventName = 'remove_from_cart'
    const items = products.map(p => {
      return {
        ...mapProductData(p, origin, eventName, {
          add_to_cart_order: digitalDataCartIndex,
          quantity: quantityDiff,
        }),
      }
    })
    const event: GA4RemoveFromCartEvent = {
      event: eventName,
      ecommerce: { currency, value, items },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushAddToWishList: (products: ProductWithCategoryType[], origin: DigitalDataOrigin) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const eventName = 'add_to_wishlist'
    const items = products.map(p => mapProductData(p, origin, eventName))
    const event: GA4AddToWishlistEvent = {
      event: eventName,
      ecommerce: { items },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushViewItemList: (products: ProductWithCategoryType[], origin: DigitalDataOrigin) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const eventName = 'view_item_list'
    const items = products.map(p => mapProductData(p, origin, eventName))
    const event: GA4ViewItemListEvent = {
      event: eventName,
      ecommerce: { items },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushSelectItem: (product: ProductWithCategoryType, origin: DigitalDataOrigin) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const eventName = 'select_item'
    const event: GA4SelectItemEvent = {
      event: eventName,
      ecommerce: { items: [mapProductData(product, origin, eventName)] },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushSelectedVariant: (interaction: 'button_click', selection: string, old_selection?: string) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    window.DigitalData.push({
      event: 'product_variant',
      interaction,
      selection,
      old_selection,
    })
  },

  pushViewPromotion: (promotions: GA4Promotion[]) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const event: GA4ViewPromotionEvent = {
      event: 'view_promotion',
      ecommerce: { items: promotions },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushSelectPromotion: (promotions: GA4Promotion[]) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const event: GA4SelectPromotionEvent = {
      event: 'select_promotion',
      ecommerce: { items: promotions },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushViewItem: (
    product: ProductWithCategoryType,
    translate: (key: string) => string,
    origin: DigitalDataOrigin
  ) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    //Legacy event
    const categoryIdPath = product?.category?.navigation?.path
      ?.map(node => node?.category?.name)
      .join('/')
      .toLowerCase()
    window.DigitalData.push({
      PageInfo: {
        BreadCrumbs: product?.category?.navigation?.path?.map(node => ({
          Name: node?.category?.name,
        })),
      },
      Products: [mapProductDataLegacy(product, categoryIdPath, translate)],
    })

    //GA4
    const eventName = 'view_item'
    const event: GA4ViewItemEvent = {
      event: eventName,
      ecommerce: { items: [mapProductData(product, origin, eventName)] },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushViewCart: (products: ProductWithCategoryType[], currency: string, value: number) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }
    const items = products.map(p => mapCartProductData(p))
    const event: GA4ViewCartEvent = {
      event: 'view_cart',
      ecommerce: { currency, value, items },
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushBeginCheckout: (
    products: ProductWithCategoryType[],
    checkoutPath: string,
    currency: string,
    value: number
  ) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const items = products.map(p => mapCartProductData(p))

    const event: BeginCheckoutType = {
      checkout_path: checkoutPath,
      ecommerce: {
        currency,
        value,
        items,
      },
      event: 'begin_checkout',
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },

  pushEventWithCartItems: (eventName: string, cartItems: CartItemFragment[]) => {
    if (!window.DigitalData) {
      window.DigitalData = []
    }

    const products = cartItems.map(
      x =>
        ({
          ...x.product,
          quantity: x.quantity,
        } as ProductWithCategoryType)
    )
    const items = products.map(p => mapCartProductData(p))

    const event = {
      ecommerce: { items },
      event: eventName,
    }

    window.DigitalData.push({ ecommerce: null } as EventObject)
    window.DigitalData.push(event)
  },
}

//TODO: fix typing for products
export const Helpers = {
  formatAndPushViewItemList: (
    products: any,
    origin: DigitalDataOrigin,
    isCampaignProduct?: boolean
  ) => {
    const f = products
      .map((p: any, i: number) => {
        return {
          ...p.searchResultProduct,
          productListIndex: i,
          label: getProductLabels(p, isCampaignProduct),
        } as ProductWithCategoryType
      })
      .filter(isNotNullOrUndefined)
    ProductDigitalData.pushViewItemList(f, origin)
  },
}
